import type { ConfigSortingOption } from '@/types/api/config'

type Pivot = ConfigSortingOption['commercializations'][number]

const pivotForCommercialization = (
  option: ConfigSortingOption,
  commercialization: string,
): Pivot | undefined =>
  option.commercializations.find((c) => c.slug === commercialization)

/**
 * Options available for a commercialization, ordered by that hub's
 * `commercialization_sorting_option.order`.
 */
export const sortingOptionsForCommercialization = (
  options: ConfigSortingOption[] | undefined,
  commercialization: string | null | undefined,
): ConfigSortingOption[] => {
  if (!options?.length || !commercialization) return []

  return options
    .filter((option) => !!pivotForCommercialization(option, commercialization))
    .sort((a, b) => {
      const orderA =
        pivotForCommercialization(a, commercialization)
          ?.commercialization_sorting_option.order ?? 0
      const orderB =
        pivotForCommercialization(b, commercialization)
          ?.commercialization_sorting_option.order ?? 0

      return orderA - orderB
    })
}

/** Slug of the option marked `default` for this commercialization. */
export const defaultSortingOptionSlug = (
  options: ConfigSortingOption[] | undefined,
  commercialization: string | null | undefined,
): string | undefined =>
  sortingOptionsForCommercialization(options, commercialization).find(
    (option) =>
      pivotForCommercialization(option, commercialization ?? '')
        ?.commercialization_sorting_option.default === true,
  )?.slug

export const isValidSortingOptionSlug = (
  options: ConfigSortingOption[] | undefined,
  slug: string,
  commercialization: string | null | undefined,
): boolean =>
  sortingOptionsForCommercialization(options, commercialization).some(
    (option) => option.slug === slug,
  )
